import React, { useState, useContext, useEffect } from "react";
import * as FaIcons from "react-icons/fa";
import { RiHomeOfficeFill } from "react-icons/ri";
import "./index.css";
import { formatCurrency, getUser } from "../../../../utils/Common";
import Swal from "sweetalert2";
import Spinner from "../../../../components/Spinner";
import { useOffice } from "../../../../hooks/useOffice";
import { useRequests } from "../../../../hooks/useRequests";
import OfficeCardContainer from "../../../../components/ui/OfficeCard/OfficeCardContainer";
import NotifyC from "../../../../components/Notifications";

const GestionEstafeta = () => {
    const user = getUser();
    const { getPostOfficeStatus, getPostOfficeMovements, getPostOfficeBalances } = useOffice();
    const { createRequest } = useRequests();
    const [infoOffice, setInfoOffice] = useState([]);
    const [newBalance, setNewBalance] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [pendingRequests, setPendingRequests] = useState({});
    const todayDate = new Date().toLocaleDateString('en-CA'); 


    const handleRequestStatusUpdate = (officeId, status) => {
        if (status === 2 || status === 3 || status === 4) {
            setPendingRequests((prev) => ({ ...prev, [officeId]: false })); 
        }
    };
    
    const handleGetInfoOffice = async (signal) => {
        try {
            const response = await getPostOfficeStatus(signal);
            const officeMovements = await getPostOfficeMovements(signal);
            const officeBalances = await getPostOfficeBalances(signal);
            const pendingRequestsData = {}; 
            const updatedInfoOffice = response.payload.map((office) => {
                const officeMov = officeMovements.payload.find((mov) => mov.IdSucursal === office.IdSucursal);
                const officeBal = officeBalances.payload.find((bal) => bal.IdSucursal === office.IdSucursal);
              
                const hasPendingRequest = officeMov?.EstatusSolicitud === 1;
                pendingRequestsData[office.IdSucursal] = hasPendingRequest;

                if (!hasPendingRequest && officeMov?.EstatusSolicitud) {
                    handleRequestStatusUpdate(office.IdSucursal, officeMov.EstatusSolicitud);
                }
    
                return {
                    id: office.IdSucursal,
                    code: office.Codigo,
                    name: office.NombreSucursal,
                    location: office.Direccion,
                    totalBalance: office.SaldoSucursal,
                    idProvincia: office.IdProvincia,
                    nombreProvincia: office.NombreProvincia,
                    IdCaja: office.IdCaja,
                    IdTipoMovimiento: officeMov?.IdTipoMovimiento,
                    FechaMovimientoCajeroType: officeMov?.FechaMovimientoCajeroType,
                    UltimoMontoAgregado: officeMov?.UltimoMontoAgregado,
                    UltimoMontoRestado: officeMov?.UltimoMontoRestado,
                    MontoMovimiento: officeMov?.MontoMovimiento,
                    TotalSaldoAgregado: officeBal?.TotalSaldoAgregado,
                    TotalSaldoRetirado: officeBal?.TotalSaldoRetirado,
                    TotalRemesasPorEntregar: officeBal?.TotalRemesasPorEntregar,
                    TotalRemesasRecibidas: officeBal?.TotalRemesasRecibidas,
                    TotalSaldoCajas: officeBal?.TotalSaldoCajas,
                };
            });
    
            setInfoOffice(updatedInfoOffice || []);
            setPendingRequests(pendingRequestsData); 
        } catch (error) {
            console.error(error);
        }
    };
       

    const handleTransaction = async (office, amount, type) => {

        // validar que la cantidad al retirar no sea mayor al saldo total de la estafeta
        if (type === 4 && office.totalBalance < parseFloat(amount)) {
            NotifyC.error('El monto a retirar no puede ser mayor al saldo total de la estafeta.');
            return;
        }
            
        const Amount = parseFloat(amount);
        if (Amount <= 0 || isNaN(Amount)) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Por favor ingrese un monto válido",
            });
            return;
        }

        const confirmMessage = type === 3 
            ? `¿Desea confirmar el monto de <strong>${formatCurrency(Amount)}</strong> para <strong>añadir</strong> a la estafeta ${office.name} ?`
            : `¿Desea confirmar el monto de <strong>${formatCurrency(Amount)}</strong> para <strong>retirar</strong> de la estafeta ${office.name} ?`
            ;
        
        const result = await Swal.fire({
            title: "Atención",
            html: confirmMessage,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "white",
            cancelButtonColor: "black",
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            customClass: {
                confirmButton: 'custom-confirm-button',
                cancelButton: 'custom-cancel-button',
            },
        });

        
        if (!result.isConfirmed) {
            return;
        }

        try{
          
            const abortController = new AbortController();
            const dataToSend = { 
                IdTipoSolicitud: 1,
                IdEstatus: 1,
                IdCaja: office.IdCaja, 
                IdSucursal: office.id,
                Accion: type,
                SaldoIngresado: Amount,
                IdProvincia: office.idProvincia,
            };
    

            const response = await createRequest(abortController.signal, dataToSend);
            if (response.code === 200) {
                const sucessMessages = {
                    3: "Tu solicitud de monto añadido ha sido enviada y está en espera de confirmación.",
                    4: "Tu solicitud de monto retirado ha sido enviada y está en espera de confirmación.",
                };
            
                Swal.fire({
                    icon: "success",
                    title: "¡Solicitud Registrada!",
                    text: sucessMessages[type],
                    confirmButtonText: "Ok",
                    customClass: {
                      confirmButton: 'custom-confirm-button',
                    }
                });
                handleGetInfoOffice();
            } else {
                const errorMessages = {
                    "ACTION_TYPE_INVALID": "Tipo de acción inválido.",
                    "CLIENT_NOT_FOUND_IN_SYSTEM": "Usuario no encontrado en el sistema.",
                    "OFFICE_NOT_FOUND" : "Estafeta no encontrada.",
                    "OFFICE_NOT_ACTIVE": "La estafeta se encuentra desactivada. Por favor, contacta al administrador para activar la estafeta.",
                    "CASHBOX_NOT_FOUND": "Caja general no encontrada.",
                    "CASHBOX_NOT_IN_OFFICE": "La caja general no pertenece a la estafeta.",
                    "ERROR_CREATING_REQUEST": "Error al crear la solicitud.",
                    "CASHBOX_HAS_PENDING_REQUEST": "La estafeta tiene una solicitud pendiente.",
               
                };
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: errorMessages[response.message] || "Hubo un error al procesar la solicitud.",
                    confirmButtonText: "Ok",
                    customClass: {
                        confirmButton: "custom-confirm-button",
                    }
                });
            }

            handleGetInfoOffice();
        
            setNewBalance(prev => ({ ...prev, [office.IdCaja]: "" }));

        }catch(error){
            Swal.fire(
                'Error!', 
                `Hubo un error al crear la solicitud`,    
                'error'
            );
            handleGetInfoOffice();
        }
    };

    const handleReloadOfficeStatus = async (officeId) => {
       setIsLoading(prev => ({ ...prev, [officeId]: true }));
       await handleGetInfoOffice();
         setIsLoading(prev => ({ ...prev, [officeId]: false }));
         Swal.fire({
            icon: "success",
            title: "Actualización exitosa",
            text: "La información de la estafeta ha sido actualizada correctamente.",
            timer: 2000,
            showConfirmButton: false,
        });
    };

    const handleNewBalanceChange = (e, officeId) => {
        const value = e.target.value;
        const regex = /^\d*(\.\d{0,2})?$/;

        if (regex.test(value)) {
            setNewBalance(prev => ({ ...prev, [officeId]: value }));
        }
    };

    const LoadingSpinner = () => {
        return (
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    padding: 20,
                    right: 0,
                    left: 0,
                    backgroundColor: "rgb(0 0 0 / 81%)",
                    height: "100vh",
                    zIndex: 1000,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "black",
                }}
                className="text-left"
            >
                <Spinner type={1} size={40} color="white" />
            </div>
        );
    };

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;
        handleGetInfoOffice(signal);
        return () => abortController.abort();
    }, []);
  
    return (
     <>
       <div className="dashboardCard text-left" style={{ color: "black", marginBottom: "20px" }}>
        <div className="d-flex justify-content-between" style={{ gap: 20 }}>
                <div className="d-flex align-items-center">
                    <RiHomeOfficeFill  size={20} className="mr-2" />
                    <h5 className="mb-0">Gestión de Estafeta</h5>
                </div>
            {user?.roleId >= 4 && (
               <div className="text-center mt-lg-0 mt-sm-3"      
                style={{
                fontSize: "1rem",
                background: "#dbf5f3",
                padding: "8px 12px",
                borderRadius: "12px",
             }}>
                <div style={{ color: "#0eab9b" }}  >Saldo total de las estafetas</div>
                <div className="text-bold" style={{ color: "#0eab9b" }}>{formatCurrency(infoOffice.reduce((acc, office) => acc + office.totalBalance, 0))}</div>
               </div>
            )}
            </div>
        </div>
        <div className="justify-content-between" style={{ color: "black" }}>
            {infoOffice.length === 0 ? (
                <LoadingSpinner />
            ) : (
                <OfficeCardContainer
                    offices={infoOffice}
                    handleTransaction={handleTransaction}
                    handleReloadOfficeStatus={handleReloadOfficeStatus}
                    newBalance={newBalance}
                    handleNewBalanceChange={handleNewBalanceChange}
                    isLoading={isLoading}
                    pendingRequests={pendingRequests}
                />
            )}
        </div>
     </>
      
    );
};

export default GestionEstafeta;
